import { Component } from '@theme/component';
import { addLines } from '@theme/dialed-cart';

/**
 * <dialed-product>: product page for add-ons, gear and parts (sections/dialed-product.liquid).
 * Option buttons pick the variant, the gallery jumps to its photo, and the button adds it to the
 * cart through addLines so the drawer opens. Pack offer cards (blocks/_dialed-pack-offers.liquid), when
 * there, replace the quantity: the checked card sets the line, price and button. Copy comes from
 * data-t-* attributes.
 */
class DialedProduct extends Component {
  /** @type {Array<{id:number,title:string,options:string[],price:number,compare:number,available:boolean,media:number}>} */
  variants = [];
  /** @type {(typeof this.variants)[number] | null} */
  variant = null;
  /** @type {{id:number,quantity:number,price:number,compare:number,available:boolean} | null} */
  offer = null;
  #slide = 0;
  #abort = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    try {
      this.variants = JSON.parse(this.querySelector('[data-product-variants]')?.textContent ?? '[]');
    } catch (error) {
      console.error('[dialed-product] Bad variant data', error);
      return;
    }
    this.variant = this.variants.find((v) => String(v.id) === this.dataset.variantId) ?? this.variants[0] ?? null;
    this.offer = this.#readOffer(this.querySelector('input[data-offer]:checked'));

    const { signal } = this.#abort;
    const { track, prev, next, cta, stickyCta } = this.refs;
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    prev?.addEventListener('click', () => this.#go(this.#slide - 1), { signal });
    next?.addEventListener('click', () => this.#go(this.#slide + 1), { signal });
    track?.addEventListener('scroll', this.#onTrackScroll, { signal, passive: true });
    cta?.addEventListener('click', () => this.#buy(), { signal });
    stickyCta?.addEventListener('click', () => this.#buy(), { signal });

    // A tier card can be picked first (buy 2 get 1): price and button follow it from the start
    if (this.offer) {
      this.#syncOffers();
      this.offer = this.#readOffer(this.querySelector('input[data-offer]:checked'));
      this.#render();
    }
    this.#updateGallery();
    this.#markUnavailable();
    this.#observeSticky();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
  }

  /* ---------- Variant ---------- */

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (input instanceof HTMLSelectElement && input.hasAttribute('data-offer-size')) {
      const radio = input.closest('.dl-offer, .dl-tier')?.querySelector('input[data-offer]');
      if (radio instanceof HTMLInputElement) radio.checked = true;
      this.#syncOffers();
      this.offer = this.#readOffer(this.querySelector('input[data-offer]:checked'));
      this.#render();
      return;
    }
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    if (input.hasAttribute('data-offer')) {
      this.offer = this.#readOffer(input);
      this.#render();
      return;
    }
    const selected = [...this.querySelectorAll('[data-option]')].map(
      (fieldset) => /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'))?.value ?? ''
    );
    this.variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i])) ?? null;

    const labels = this.refs.optionValue;
    if (Array.isArray(labels)) labels.forEach((label, i) => (label.textContent = selected[i] ?? ''));

    if (this.variant) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(this.variant.id));
      window.history.replaceState({}, '', url);
      if (this.variant.media) {
        const index = this.#slides().findIndex((slide) => slide.dataset.mediaId === String(this.variant?.media));
        if (index >= 0) this.#go(index);
      }
    }
    this.#markUnavailable();
    this.#syncOffers();
    this.#syncTierImages();
    this.offer = this.#readOffer(this.querySelector('input[data-offer]:checked'));
    this.#render();
  };

  /** Tier cards show the chosen color's photo */
  #syncTierImages() {
    const data = this.querySelector('[data-tier-images]');
    if (!data || !this.variant) return;
    /** @type {Record<string, {src: string, srcset: string}>} */
    let images = {};
    try {
      images = JSON.parse(data.textContent ?? '{}');
    } catch {
      return;
    }
    const image = images[String(this.variant.id)];
    if (!image) return;
    for (const img of /** @type {NodeListOf<HTMLImageElement>} */ (this.querySelectorAll('img[data-tier-img]'))) {
      img.srcset = image.srcset;
      img.src = image.src;
    }
  }

  /** Keeps the offer cards on the chosen variant, and the set on the same size (or the one picked on its card) */
  #syncOffers() {
    const v = this.variant;
    for (const input of /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('input[data-offer]'))) {
      const card = input.closest('.dl-offer, .dl-tier');
      if (!card) continue;
      /** @type {{id:number,price:number,compare:number,available:boolean} | null | undefined} */
      let line = null;
      if (input.dataset.offer === 'set') {
        /** @type {Array<{id:number,title:string,price:number,compare:number,available:boolean}>} */
        let sets = [];
        try {
          sets = JSON.parse(card.querySelector('[data-offer-variants]')?.textContent ?? '[]');
        } catch {
          continue;
        }
        const picker = card.querySelector('select[data-offer-size]');
        line = sets.find((s) => (picker instanceof HTMLSelectElement ? String(s.id) === picker.value : s.title === v?.title));
      } else if (v) {
        // Tier cards (buy 2 get 1): pay for some units, the rest are free
        const qty = Number(input.dataset.offerQty) || 1;
        const paid = Number(input.dataset.offerPaid) || qty;
        const percent = Number(input.dataset.offerPercent) || 0;
        const unit = Math.max(v.compare, v.price);
        const compare = input.dataset.offer === 'tier' || percent ? unit * qty : v.compare * qty;
        // Shopify takes the percent off each unit, rounded down to the cent
        const price = (v.price - Math.floor((v.price * percent) / 100)) * paid;
        line = { id: v.id, price, compare, available: v.available };
      }
      if (!line) continue;

      input.dataset.offerId = String(line.id);
      input.dataset.offerPrice = String(line.price);
      input.dataset.offerCompare = String(line.compare);
      input.dataset.offerAvailable = String(line.available);
      input.disabled = !line.available;

      const save = line.compare > line.price ? line.compare - line.price : 0;
      const now = card.querySelector('.dl-offer__now');
      const was = card.querySelector('.dl-offer__was');
      const saveEl = card.querySelector('.dl-offer__save, [data-offer-save]');
      const each = card.querySelector('[data-offer-each]');
      if (now) now.textContent = this.#money(line.price);
      if (was instanceof HTMLElement) {
        was.hidden = !save;
        was.textContent = save ? this.#money(line.compare) : '';
      }
      if (saveEl instanceof HTMLElement) {
        saveEl.hidden = !save;
        saveEl.textContent = save ? this.#t('Save', { amount: this.#money(save) }) : '';
      }
      if (each instanceof HTMLElement) {
        const units = Number(each.dataset.offerEach) || 1;
        each.textContent = this.#t('Each', { amount: this.#money(Math.floor(line.price / units)) });
      }
    }
  }

  /** Strikes through values that have no available variant with the other options as chosen */
  #markUnavailable() {
    const fieldsets = [...this.querySelectorAll('[data-option]')];
    const selected = fieldsets.map(
      (fieldset) => /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'))?.value ?? ''
    );
    fieldsets.forEach((fieldset, position) => {
      for (const input of fieldset.querySelectorAll('input')) {
        const option = /** @type {HTMLInputElement} */ (input);
        const available = this.variants.some(
          (v) => v.available && v.options.every((value, i) => (i === position ? value === option.value : value === selected[i]))
        );
        option.closest('label')?.toggleAttribute('data-unavailable', !available);
      }
    });
  }

  /** @param {Element | null} input */
  #readOffer(input) {
    if (!(input instanceof HTMLInputElement)) return null;
    const { offerId, offerQty, offerPrice, offerCompare, offerAvailable } = input.dataset;
    return {
      id: Number(offerId),
      quantity: Number(offerQty) || 1,
      price: Number(offerPrice) || 0,
      compare: Number(offerCompare) || 0,
      available: offerAvailable === 'true',
    };
  }

  #render() {
    const r = this.refs;
    const v = this.offer ?? this.variant;
    const ready = Boolean(v?.available);

    if (v && r.price) {
      r.price.textContent = this.#money(v.price);
      const save = v.compare > v.price ? v.compare - v.price : 0;
      if (r.compare) {
        r.compare.hidden = !save;
        r.compare.textContent = save ? this.#money(v.compare) : '';
      }
      if (r.save) {
        r.save.hidden = !save;
        r.save.textContent = save ? this.#t('Save', { amount: this.#money(save) }) : '';
      }
      if (r.affirm) {
        const min = Number(this.dataset.affirmMin);
        const months = Number(this.dataset.affirmMonths) || 12;
        r.affirm.hidden = min < 0 || v.price < min;
        if (r.monthly) r.monthly.textContent = this.#money(Math.round(v.price / months), { trim: false });
      }
    }

    let label = this.#t('SoldOut');
    if (!v) label = this.#t('Unavailable');
    else if (ready) label = this.#t('Cta').replaceAll('[price]', this.#money(v.price));
    if (r.ctaLabel) r.ctaLabel.textContent = label;
    if (r.cta) r.cta.disabled = !ready;
    if (r.stickyCta) r.stickyCta.disabled = !ready;
    if (r.stickyPrice && v) r.stickyPrice.textContent = this.#money(v.price);
    if (r.stickySub && this.variant && this.querySelector('[data-option]')) r.stickySub.textContent = this.variant.title;
  }

  /* ---------- Cart ---------- */

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const thumb = target?.closest('[data-slide]');
    if (thumb instanceof HTMLElement) this.#go(Number(thumb.dataset.slide));

    const step = target?.closest('[data-qty]');
    if (step instanceof HTMLElement && this.refs.qty instanceof HTMLInputElement) {
      const qty = this.refs.qty;
      qty.value = String(Math.min(99, Math.max(1, (Number(qty.value) || 1) + Number(step.dataset.qty))));
    }
  };

  async #buy() {
    const { cta, stickyCta, live, added, addedText } = this.refs;
    const v = this.offer ?? this.variant;
    if (!v || !v.available) return;
    const qtyInput = this.offer ? null : this.refs.qty instanceof HTMLInputElement ? this.refs.qty : null;
    const quantity = this.offer?.quantity ?? Math.min(99, Math.max(1, Number(qtyInput?.value) || 1));

    for (const button of [cta, stickyCta]) {
      if (!button) continue;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    const { ok } = await addLines(this, [{ id: v.id, quantity }], { sourceName: 'dialed-product' });
    for (const button of [cta, stickyCta]) {
      if (!button) continue;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }

    // A set card adds another product: name the set, not this page's product
    const setCard = this.querySelector('input[data-offer="set"]:checked')?.closest('.dl-offer');
    const setTitle = setCard?.querySelector('.dl-offer__title')?.firstChild?.textContent?.trim();
    const title = setTitle || (this.querySelector('.dl-buy-intro__title')?.textContent?.trim() ?? '');
    const message = ok ? this.#t('Added', { title }) : this.#t('Error');
    if (live) live.textContent = message;
    if (added && addedText) {
      addedText.textContent = message;
      added.hidden = false;
    }
    if (qtyInput && ok) qtyInput.value = '1';
  }

  /* ---------- Gallery ---------- */

  /** @returns {HTMLElement[]} */
  #slides() {
    return /** @type {HTMLElement[]} */ ([...(this.refs.track?.children ?? [])]);
  }

  /** @param {number} index */
  #go(index) {
    const slides = this.#slides();
    if (!slides.length || !this.refs.track) return;
    this.#slide = Math.max(0, Math.min(index, slides.length - 1));
    this.refs.track.scrollTo({ left: this.#slide * this.refs.track.clientWidth, behavior: 'smooth' });
    this.#updateGallery();
  }

  #onTrackScroll = () => {
    const { track } = this.refs;
    if (!track) return;
    const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
    if (index !== this.#slide) {
      this.#slide = index;
      this.#updateGallery();
    }
  };

  #updateGallery() {
    const total = this.#slides().length;
    if (this.refs.count) this.refs.count.textContent = total > 1 ? `${this.#slide + 1} / ${total}` : '';
    if (this.refs.prev) this.refs.prev.disabled = this.#slide === 0;
    if (this.refs.next) this.refs.next.disabled = this.#slide >= total - 1;
    /** @type {HTMLElement | null} */
    let active = null;
    for (const thumb of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-slide]'))) {
      const current = Number(thumb.dataset.slide) === this.#slide;
      thumb.setAttribute('aria-current', String(current));
      if (current) active = thumb;
    }
    // Keep the current thumbnail in view when the strip scrolls (more than five photos)
    const strip = active?.parentElement;
    if (active && strip && strip.scrollWidth > strip.clientWidth + 1) {
      const left = active.offsetLeft - (strip.clientWidth - active.offsetWidth) / 2;
      strip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    }
  }

  /* ---------- Sticky bar ---------- */

  /** @type {number | null} */
  #stickyRaf = null;

  /** Shows the bar once the button has scrolled above the viewport, hides it near the footer */
  #observeSticky() {
    const { sticky, cta } = this.refs;
    if (!sticky || !cta || this.dataset.sticky !== 'true') return;
    const footer = document.querySelector('.page-wrapper > footer, footer');
    const update = () => {
      this.#stickyRaf = null;
      const ctaAbove = cta.getBoundingClientRect().bottom < 0;
      const footerVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      const show = ctaAbove && !footerVisible;
      if (sticky.hidden === show) sticky.hidden = !show;
    };
    const onScroll = () => {
      this.#stickyRaf ??= requestAnimationFrame(update);
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true, signal: this.#abort.signal });
    window.addEventListener('resize', onScroll, { passive: true, signal: this.#abort.signal });
    update();
  }

  /* ---------- Helpers ---------- */

  /**
   * @param {string} key
   * @param {Record<string, string>} [values]
   */
  #t(key, values = {}) {
    let text = this.dataset[`t${key}`] ?? '';
    for (const [k, v] of Object.entries(values)) text = text.replaceAll(`[${k}]`, String(v));
    return text;
  }

  /**
   * @param {number} cents
   * @param {{ trim?: boolean }} [options]
   */
  #money(cents, { trim = true } = {}) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const [whole, dec] = value.split('.');
    const commas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const amount = trim && dec === '00' ? commas : `${commas}.${dec}`;
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, commas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/<[^>]+>/g, '');
  }
}

if (!customElements.get('dialed-product')) customElements.define('dialed-product', DialedProduct);
